import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, TrendingUp, Users, Percent, XCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useLanguage } from "../../i18n/LanguageContext";
import api from "../../lib/api";

const MONTH_NAMES_NL = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];
const MONTH_NAMES_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MONTH_SHORT_NL = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
const MONTH_SHORT_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function AdminReports() {
  const { t, lang } = useLanguage();
  const now = new Date();
  const [viewMode, setViewMode] = useState("month");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [report, setReport] = useState(null);

  const load = useCallback(() => {
    let cancelled = false;
    const endpoint = viewMode === "month" ? "/admin/dashboard/monthly-report" : "/admin/dashboard/yearly-report";
    const params = viewMode === "month" ? { year, month } : { year };
    api.get(endpoint, { params })
      .then(({ data }) => { if (!cancelled) setReport(data); })
      .catch(() => { if (!cancelled) setReport(null); });
    return () => { cancelled = true; };
  }, [viewMode, year, month]);

  useEffect(() => load(), [load]);

  const shiftMonth = (delta) => {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth > 12) { newMonth = 1; newYear += 1; }
    if (newMonth < 1) { newMonth = 12; newYear -= 1; }
    setMonth(newMonth);
    setYear(newYear);
  };

  const periodLabel = viewMode === "month" ? `${(lang === "nl" ? MONTH_NAMES_NL : MONTH_NAMES_EN)[month - 1]} ${year}` : `${year}`;
  const chartData = viewMode === "month"
    ? (report?.by_day || []).map((d) => ({ label: d.date.slice(8, 10), revenue: d.revenue }))
    : (report?.by_month || []).map((m) => ({ label: (lang === "nl" ? MONTH_SHORT_NL : MONTH_SHORT_EN)[m.month - 1], revenue: m.revenue }));

  return (
    <div data-testid="admin-reports-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-serif text-2xl sm:text-3xl text-white">{t("admin.reports.title")}</h1>
        <div className="flex items-center bg-white/5 rounded-md p-1" data-testid="reports-view-toggle">
          <button
            data-testid="reports-view-month"
            onClick={() => setViewMode("month")}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${viewMode === "month" ? "bg-gold text-white" : "text-white/50 hover:text-white"}`}
          >
            {t("admin.reports.monthView")}
          </button>
          <button
            data-testid="reports-view-year"
            onClick={() => setViewMode("year")}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${viewMode === "year" ? "bg-gold text-white" : "text-white/50 hover:text-white"}`}
          >
            {t("admin.reports.yearView")}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 mb-8">
        <button
          data-testid="reports-prev-period"
          onClick={() => (viewMode === "month" ? shiftMonth(-1) : setYear((y) => y - 1))}
          title={viewMode === "month" ? t("admin.reports.prevMonth") : t("admin.reports.prevYear")}
          className="p-2 rounded-md bg-white/5 text-white/60 hover:text-gold hover:bg-white/10"
        >
          <ChevronLeft size={18} />
        </button>
        <p data-testid="reports-current-period" className="text-white text-sm font-medium min-w-[120px] text-center capitalize">{periodLabel}</p>
        <button
          data-testid="reports-next-period"
          onClick={() => (viewMode === "month" ? shiftMonth(1) : setYear((y) => y + 1))}
          title={viewMode === "month" ? t("admin.reports.nextMonth") : t("admin.reports.nextYear")}
          className="p-2 rounded-md bg-white/5 text-white/60 hover:text-gold hover:bg-white/10"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testId="reports-total-appointments" icon={Users} label={t("admin.reports.totalAppointments")} value={report?.total_appointments ?? "-"} />
        <StatCard testId="reports-total-revenue" icon={TrendingUp} label={t("admin.reports.totalRevenue")} value={report ? `€${report.total_revenue.toFixed(0)}` : "-"} />
        <StatCard testId="reports-average-price" icon={Percent} label={t("admin.reports.averagePrice")} value={report ? `€${report.average_price.toFixed(2)}` : "-"} />
        <StatCard testId="reports-cancelled-count" icon={XCircle} label={t("admin.reports.cancelledCount")} value={report?.cancelled_count ?? "-"} />
      </div>

      {report && report.total_appointments === 0 ? (
        <p data-testid="reports-no-data" className="text-white/40 text-sm">{t("admin.reports.noData")}</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-[#141414] border border-white/10 rounded-lg p-5">
            <h2 className="text-white text-lg mb-4">{viewMode === "month" ? t("admin.reports.chartTitle") : t("admin.reports.chartTitleYear")}</h2>
            <div style={{ width: "100%", height: 260, minWidth: 0 }} data-testid="reports-revenue-chart">
              <ResponsiveContainer minWidth={200} minHeight={200}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="label" stroke="rgba(255,255,255,0.4)" fontSize={12} />
                  <YAxis stroke="rgba(255,255,255,0.4)" fontSize={12} />
                  <Tooltip contentStyle={{ backgroundColor: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} formatter={(v) => [`€${v}`, t("admin.reports.revenue")]} />
                  <Bar dataKey="revenue" fill="#D4AF37" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-[#141414] border border-white/10 rounded-lg p-5">
            <h2 className="text-white text-lg mb-4">{t("admin.reports.byService")}</h2>
            <table className="w-full text-sm" data-testid="reports-by-service-table">
              <thead>
                <tr className="text-left text-white/40 text-xs uppercase border-b border-white/10">
                  <th className="pb-2">{t("admin.reports.service")}</th>
                  <th className="pb-2 text-right">{t("admin.reports.count")}</th>
                  <th className="pb-2 text-right">{t("admin.reports.revenue")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {(report?.by_service || []).map((s) => (
                  <tr key={s.service_name} data-testid={`reports-service-row-${s.service_name}`} className="text-white/80">
                    <td className="py-2.5">{s.service_name}</td>
                    <td className="py-2.5 text-right">{s.count}</td>
                    <td className="py-2.5 text-right text-gold font-medium">€{s.revenue.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="bg-[#141414] border border-white/10 rounded-lg p-5">
    <Icon size={18} className="text-gold mb-3" />
    <p className="text-2xl text-white font-serif">{value}</p>
    <p className="text-white/40 text-xs mt-1">{label}</p>
  </div>
);
